export enum PlatformType {
  Xero = 1,
  XeroXpm = 2,
  XeroWorkflowMax = 3,
  Myob = 4,
  WorkGuru = 5,
  BlueRockWorkflowMax = 6,
  Karbon = 7,
  FYI = 8,
  QuickBooks = 9,
}

export enum DataSource {
  XpmJob     = 1,
  XpmJobTask = 2,
  FYI        = 3,
  Karbon     = 4,
}

export enum StaffAssignedDataPoint {
  XpmJobManager = 1,
  XpmJobStaff = 2,
  DashboardInsightsOnly = 3,
  XpmJobTaskStaff = 4,
  FyiJobManager = 5,
}

export type BudgetMode = 'hours' | 'dollars'
export type Granularity = 'weekly' | 'monthly'

export interface PagedResult<T> {
  items: T[]
  totalCount: number
  page: number
  pageSize: number
}

export interface WorkflowJobsResult extends PagedResult<WorkflowJob> {
  stateOptions: string[]
  staffOptions: WorkflowStaffOption[]
}

export interface AllocationCandidatesResult extends PagedResult<AllocationJob> {
  stateOptions: string[]
}

export interface WorkflowStaffOption {
  staffId: string
  staffName: string
}

export interface CapacitySettings {
  settingsId: string
  dataSource: DataSource
  staffAssignedDataPoint: StaffAssignedDataPoint
  budgetMode: BudgetMode
  defaultGranularity: Granularity
  targetUtilisationPct: number
}

export interface SaveSettingsPayload {
  dataSource: DataSource
  staffAssignedDataPoint: StaffAssignedDataPoint
  budgetMode: BudgetMode
  defaultGranularity: Granularity
  targetUtilisationPct: number
}

export interface CapacityPeriod {
  periodLabel: string
  weekStart: string
  periodStart?: string
  periodEnd?: string
  committedHours: number
  availableHours: number
  committedAmount: number
  availableAmount: number
  utilisationPct: number
}

export type UtilisationStatus =
  | 'optimal'
  | 'near-capacity'
  | 'over-allocated'
  | 'under-utilised'
  | 'burnout-risk'
  | 'unavailable'

export interface ClientCapacity {
  clientName: string
  hours: number
  availableHours: number
  committedHours: number
  usedHours: number
  amount: number
  availableAmount: number
  committedAmount: number
  usedAmount: number
  pct: number | null
  jobs: ClientJobCapacity[]
}

export interface ClientJobCapacity {
  jobId: string
  jobName: string
  hours: number
  committedHours: number
  usedHours: number
  amount: number
  committedAmount: number
  usedAmount: number
  pct: number | null
}

export interface StaffUtilisation {
  staffId: string
  staffName: string
  team: string
  availableHours: number
  committedHours: number
  usedHours: number
  availableAmount: number
  committedAmount: number
  usedAmount: number
  utilisationPct: number | null
  status: UtilisationStatus
  clients: ClientCapacity[]
}

export interface CapacityKpis {
  totalAvailableHours: number
  totalAvailableAmount: number
  avgUtilisationPct: number | null
  overCapacityCount: number
}

export interface CapacityDashboardData {
  chart: CapacityPeriod[]
  utilisation: StaffUtilisation[]
  teamOptions: string[]
  utilisationTotalCount: number
  utilisationPage: number
  utilisationPageSize: number
  kpis: CapacityKpis
}

export type JobStatus = 'on-track' | 'due-soon' | 'over-budget' | 'needs-staff' | 'blocked'

export interface JobStaffSummary {
  staffId: string
  staffName: string
  allocatedHours: number
  usedHours: number
  allocatedAmount: number
  usedAmount: number
  origin?: string
  isReadOnly?: boolean
}

export interface SourceAllocationStaff {
  staffId: string
  staffName: string
  unscheduledHours?: number | null
  unscheduledAmount?: number | null
}

export interface WorkflowJob {
  jobId: string
  budgetJobId?: string | null
  clientName: string
  jobName: string
  budgetHours: number | null
  usedHours: number
  budgetAmount: number | null
  usedAmount: number
  progressPct: number
  dueDate: string | null
  status: JobStatus
  isRecurring: boolean
  state: string
  budgetSource?: string
  xpmJobNumber?: string
  staff: JobStaffSummary[]
}

export type AllocationStatus = 'unassigned' | 'unscheduled' | 'needs-reallocation' | 'no-budget' | 'allocated'

export interface AllocationJob extends WorkflowJob {
  allocatedHours: number
  remainingHours: number | null
  allocatedAmount: number
  remainingAmount: number | null
  allocationStatus: AllocationStatus
  planningHours?: number
  planningAmount?: number
  origin?: string
  isReadOnly?: boolean
  budgetSource?: string
  notes: string | null
}

export interface StaffAvailability {
  staffId: string
  staffName: string
  weekStart: string
  availableHours: number
  committedHours: number
  freeHours: number
  availableAmount: number
  committedAmount: number
  freeAmount: number
}

export interface JobAllocation {
  allocationId: string
  jobId: string
  jobName: string
  clientName: string
  staffId: string
  staffName: string
  weekStart: string
  granularity?: Granularity
  allocationMode: BudgetMode
  allocatedHours: number | null
  allocatedAmount: number | null
  notes: string | null
}

export interface SaveAllocationPayload {
  jobId: string
  staffId: string
  weekStart: string
  allocatedHours?: number | null
  allocatedAmount?: number | null
  notes?: string | null
}

export interface SaveAllocationsOptions {
  replaceJobAllocations?: boolean
  replaceStaffAllocations?: boolean
  jobId?: string
  staffId?: string
  jobIds?: string[]
  weekStart?: string
  weekEnd?: string
  jobNote?: JobNotePayload
}

export interface ReplaceJobAllocationsPayload {
  jobId: string
  weekStart: string
  weekEnd: string
  allocations: SaveAllocationPayload[]
  jobNote?: JobNotePayload
}

export interface RemoveSourceAllocationStaffPayload {
  jobId: string
  staffId: string
}

export interface ValidateSourceAllocationStaffRemovalPayload {
  jobId: string
  staffIds: string[]
}

export interface SaveJobBudgetPayload {
  jobId: string
  budgetMode: BudgetMode
  budgetValue: number
  notes?: string | null
}

export interface JobNotePayload {
  jobId: string
  notes: string | null
}

export interface CapacityJobBudget {
  budgetId: string | null
  jobId: string
  budgetMode: BudgetMode
  granularity?: Granularity
  budgetValue: number
  budgetSource: string
  queuedSync: boolean
}

export interface AllocationScopeRecommendation {
  jobId: string
  suggestedBudgetHours: number | null
  basis: string
}

export interface AllocationStaffRecommendation {
  jobId: string
  staffId: string
  staffName: string
  fitScore: number
  reason: string
}

export interface AllocationScheduleRecommendation {
  jobId: string
  staffId: string
  weekStart: string
  hours: number
}

export interface AllocationRecommendation {
  warning: string | null
  scopes: AllocationScopeRecommendation[]
  staffRecommendations: AllocationStaffRecommendation[]
  schedule: AllocationScheduleRecommendation[]
}

export interface AllocationAssignment {
  jobId: string
  staffId: string
  hours: number
}

export interface AllocationScheduleItem {
  jobId: string
  staffId: string
  weekStart: string
  hours: number
}

export interface AllocationWizardState {
  step: 1 | 2 | 3
  selectedJobIds: string[]
  assignments: AllocationAssignment[]
  schedule: AllocationScheduleItem[]
  notes: string
}
